"use client";

import { useState, useEffect, useRef, FormEvent, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Check,
  Clock,
  Share2,
  Brain,
  Target,
  Search,
  PenLine,
  Video,
  Film,
  Clapperboard,
  ImagePlay,
  Palette,
  Printer,
  Code2,
  Store,
  Smartphone,
  Layers,
  Server,
  Megaphone,
  PenTool,
  MonitorSmartphone,
  type LucideIcon,
} from "lucide-react";
import { Glow } from "@/components/ui/Glow";
import { useLang } from "@/lib/LanguageContext";
import { sendForm } from "@/lib/sendForm";
import { FormGuard, type FormGuardHandle } from "@/components/ui/FormGuard";
import { services as allServices, type ServiceCategory } from "@/data/services";

const STORAGE_KEY = "contact_page_draft";
const PHONE_DISPLAY = "+359 886 66 20 20";
const PHONE_HREF = "tel:+359886662020";
const EMAIL = "office@avivadigital.bg";
const EASE = [0.22, 1, 0.36, 1] as const;

const ICON_MAP: Record<string, LucideIcon> = {
  Share2,
  Brain,
  Target,
  Mail,
  Search,
  PenLine,
  Video,
  Film,
  Clapperboard,
  ImagePlay,
  Palette,
  Printer,
  Code2,
  Store,
  Smartphone,
  Layers,
  Server,
};

type FormStatus = "idle" | "loading" | "success" | "error";

interface FieldState {
  name: string;
  phone: string;
  email: string;
  company: string;
  message: string;
  services: string[];
}

const EMPTY_FIELDS: FieldState = {
  name: "",
  phone: "",
  email: "",
  company: "",
  message: "",
  services: [],
};

function loadDraft(): FieldState {
  if (typeof window === "undefined") return EMPTY_FIELDS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...EMPTY_FIELDS, ...JSON.parse(raw) } : EMPTY_FIELDS;
  } catch {
    return EMPTY_FIELDS;
  }
}

const CONTROL =
  "w-full rounded-xl border border-white/[0.08] bg-white/[0.03] text-[16px] text-white placeholder:text-white/25 outline-none transition-[border-color,background-color,box-shadow] duration-200 hover:border-white/15 focus:border-[#FFB76C]/50 focus:bg-white/[0.05] focus:ring-4 focus:ring-[#FF419D]/10";
const INPUT = `${CONTROL} h-12 px-4`;

function Field({
  id,
  label,
  required,
  optional,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  optional?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="flex items-baseline gap-1.5 text-[13px] font-medium text-white/60">
        {label}
        {required && <span className="text-[#FF419D]">*</span>}
        {optional && <span className="text-[12px] font-normal text-white/30">· {optional}</span>}
      </label>
      {children}
    </div>
  );
}

function ContactRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  href?: string;
}) {
  const inner = (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#FF419D]/20 bg-gradient-to-br from-[#FFB76C]/15 to-[#FF419D]/15">
        <Icon size={18} className="text-[#FF6FB5]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-white/40">{label}</span>
        <span className="block truncate text-[15px] text-white">{value}</span>
      </span>
      {href && (
        <ArrowUpRight
          size={16}
          className="shrink-0 text-white/30 transition-all duration-200 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      )}
    </>
  );
  const cls = "group flex min-h-[64px] items-center gap-4 rounded-2xl px-3 py-2.5 transition-colors duration-200";
  return href ? (
    <a href={href} className={`${cls} hover:bg-white/[0.04]`}>
      {inner}
    </a>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

const CATEGORY_ICONS: Record<ServiceCategory, LucideIcon> = {
  marketing: Megaphone,
  creative: PenTool,
  web: MonitorSmartphone,
};

interface ServiceOption {
  title: string;
  icon: LucideIcon;
}

interface ServiceGroup {
  id: ServiceCategory;
  label: string;
  options: ServiceOption[];
}

function ServicePicker({
  label,
  placeholder,
  hint,
  doneLabel,
  clearLabel,
  groups,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  placeholder: string;
  hint: string;
  doneLabel: string;
  clearLabel: string;
  groups: ServiceGroup[];
  selected: string[];
  onToggle: (title: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ServiceCategory>(groups[0]?.id ?? "marketing");
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Open on the tab that holds the first selected service
  const openPanel = () => {
    const first = groups.find((g) => g.options.some((o) => selected.includes(o.title)));
    if (first) setTab(first.id);
    setOpen(true);
  };

  const close = (refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = groups.find((g) => g.id === tab) ?? groups[0];

  return (
    <div ref={rootRef} className="relative flex flex-col gap-2">
      <span id="services-label" className="text-[13px] font-medium text-white/60">
        {label}
      </span>

      {/* Trigger — fixed height, so selecting never shifts the layout */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="services-panel"
        aria-labelledby="services-label"
        onClick={() => (open ? close() : openPanel())}
        className={`${CONTROL} flex h-12 items-center gap-3 px-4 text-left cursor-pointer ${
          open ? "border-[#FFB76C]/50 bg-white/[0.05] ring-4 ring-[#FF419D]/10" : ""
        }`}
      >
        <span className={`min-w-0 flex-1 truncate text-[15px] ${selected.length ? "text-white" : "text-white/25"}`}>
          {selected.length ? selected.join(", ") : placeholder}
        </span>
        {selected.length > 0 && (
          <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#FF419D] to-[#FFB76C] px-2 text-[12px] font-semibold text-white">
            {selected.length}
          </span>
        )}
        <ChevronDown
          size={17}
          className={`shrink-0 text-white/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Floating panel — overlays the form instead of pushing it down */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="services-panel"
            role="dialog"
            aria-labelledby="services-label"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="absolute inset-x-0 top-full z-40 mt-2 origin-top overflow-hidden rounded-2xl border border-white/10 bg-[#141419]/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] backdrop-blur-xl"
          >
            {/* Category tabs */}
            <div className="p-2 pb-0">
              <div role="tablist" className="grid grid-cols-3 gap-1 rounded-xl bg-white/[0.04] p-1">
                {groups.map((g) => {
                  const count = g.options.filter((o) => selected.includes(o.title)).length;
                  const isActive = g.id === active.id;
                  const TabIcon = CATEGORY_ICONS[g.id];
                  return (
                    <button
                      key={g.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setTab(g.id)}
                      className={`group relative flex min-h-[40px] flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 rounded-lg px-2 py-1.5 text-center text-[12.5px] sm:text-[13px] font-medium leading-tight transition-colors duration-200 cursor-pointer ${
                        isActive ? "text-white" : "text-white/45 hover:text-white/80"
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="service-tab"
                          className="absolute inset-0 rounded-lg border border-white/10 bg-white/[0.08]"
                          transition={{ duration: 0.25, ease: EASE }}
                        />
                      )}
                      <TabIcon
                        size={15}
                        strokeWidth={1.75}
                        aria-hidden
                        className={`relative shrink-0 transition-colors duration-200 ${
                          isActive ? "text-[#FF6FB5]" : "text-white/35 group-hover:text-white/70"
                        }`}
                      />
                      <span className="relative flex items-center gap-1.5">
                        {g.label}
                        {count > 0 && (
                          <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#FF419D] px-1 text-[10.5px] font-semibold text-white">
                            {count}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="px-2 pt-3 text-[12.5px] text-white/35">{hint}</p>
            </div>

            {/* Options for the active category */}
            <ul
              role="tabpanel"
              className="grid max-h-[min(288px,45vh)] grid-cols-1 gap-1 overflow-y-auto overscroll-contain p-2 sm:grid-cols-2"
            >
              {active.options.map(({ title, icon: Icon }) => {
                const on = selected.includes(title);
                return (
                  <li key={title}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      onClick={() => onToggle(title)}
                      className={`group flex w-full min-h-[52px] items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors duration-150 cursor-pointer ${
                        on
                          ? "border-[#FF419D]/35 bg-[#FF419D]/[0.09]"
                          : "border-transparent hover:bg-white/[0.04]"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
                          on ? "bg-[#FF419D]/20 text-[#FF8FC6]" : "bg-white/[0.05] text-white/50 group-hover:text-white/80"
                        }`}
                      >
                        <Icon size={17} />
                      </span>
                      <span className={`flex-1 text-[14.5px] leading-snug ${on ? "text-white" : "text-white/75"}`}>
                        {title}
                      </span>
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors duration-150 ${
                          on ? "border-transparent bg-gradient-to-br from-[#FF419D] to-[#FFB76C]" : "border-white/20"
                        }`}
                      >
                        {on && <Check size={13} strokeWidth={3} className="text-white" />}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] px-3 py-2.5">
              <button
                type="button"
                onClick={onClear}
                disabled={selected.length === 0}
                className="min-h-[36px] rounded-lg px-2 text-[13px] text-white/45 transition-colors hover:text-white disabled:opacity-0 cursor-pointer"
              >
                {clearLabel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                className="min-h-[36px] rounded-lg bg-white px-4 text-[13px] font-semibold text-[#0A0A0F] transition-opacity hover:opacity-90 cursor-pointer"
              >
                {doneLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ContactPage() {
  const { t } = useLang();
  const c = t.contactForm;
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const guard = useRef<FormGuardHandle>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [fields, setFields] = useState<FieldState>(EMPTY_FIELDS);

  // Hydrate from localStorage after mount
  useEffect(() => {
    setFields(loadDraft());
  }, []);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fields));
    } catch {}
  }, [fields]);

  const setField =
    (key: keyof Omit<FieldState, "services">) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFields((f) => ({ ...f, [key]: e.target.value }));

  const selectedServices = fields.services;

  const toggleService = (title: string) => {
    setFields((f) => ({
      ...f,
      services: f.services.includes(title)
        ? f.services.filter((s) => s !== title)
        : [...f.services, title],
    }));
  };

  // Every service, grouped by category (a title shared by two categories is listed once).
  const seen = new Set<string>();
  const serviceGroups: ServiceGroup[] = t.categoriesList.map((cat) => ({
    id: cat.id as ServiceCategory,
    label: cat.label,
    options: allServices
      .filter((s) => s.category === cat.id)
      .map((s) => ({
        title: t.servicesList.find((ts) => ts.id === s.id)?.title ?? s.title,
        icon: ICON_MAP[s.icon] ?? Layers,
      }))
      .filter(({ title }) => (seen.has(title) ? false : (seen.add(title), true))),
  }));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("loading");
    setErrorMessage("");

    try {
      await sendForm(
        "contact",
        {
          name: fields.name,
          phone: fields.phone,
          email: fields.email,
          company: fields.company,
          services: fields.services.join(", "),
          message: fields.message,
        },
        guard.current?.get() ?? { token: "", trap: "" },
      );
      setFormStatus("success");
      setFields(EMPTY_FIELDS);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    } catch {
      setFormStatus("error");
      setErrorMessage(c.errorFull);
    }
    guard.current?.reset();
  };

  return (
    <section className="relative pb-16 sm:pb-24">
      <Glow color="pink" size={700} className="top-1/3 left-1/4" />
      <Glow color="orange" size={420} className="bottom-0 right-1/4" />

      <div className="container relative z-10">
        <div className="mx-auto grid max-w-6xl grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start">
          {/* ── Form ── */}
          <motion.div
            className="lg:col-span-8 relative rounded-[22px] sm:rounded-[28px] border border-white/[0.07] bg-[#0E0E14]/70 p-5 sm:p-8 lg:p-10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.8)] backdrop-blur-xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <span
              aria-hidden
              className="absolute top-0 left-1/4 right-1/4 h-px"
              style={{ background: "linear-gradient(90deg, transparent, rgba(255,183,108,0.5), rgba(224,64,160,0.5), transparent)" }}
            />

            <AnimatePresence mode="wait" initial={false}>
              {formStatus === "success" ? (
                <motion.div
                  key="success"
                  className="flex flex-col items-center justify-center gap-4 py-14 sm:py-20 text-center"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                >
                  <motion.span
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#FFB76C] to-[#E040A0] shadow-[0_0_60px_-10px_rgba(224,64,160,0.7)]"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
                  >
                    <CheckCircle size={38} className="text-white" />
                  </motion.span>
                  <h3 className="font-display text-2xl sm:text-3xl text-white">{c.successTitle}</h3>
                  <p className="max-w-xs text-base text-white/60">{c.successSub}</p>
                  <button
                    type="button"
                    onClick={() => setFormStatus("idle")}
                    className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-white/10 px-5 text-sm text-white/80 transition-colors hover:border-white/25 hover:text-white cursor-pointer"
                  >
                    {c.sendAnother}
                    <ArrowRight size={14} />
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6 sm:gap-7"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl text-white">{c.title}</h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5">
                    <Field id="name" label={c.fullName} required>
                      <input
                        id="name"
                        name="name"
                        required
                        autoComplete="name"
                        enterKeyHint="next"
                        value={fields.name}
                        onChange={setField("name")}
                        placeholder={c.firstNamePlaceholder}
                        className={INPUT}
                      />
                    </Field>
                    <Field id="phone" label={c.phone} required>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        required
                        autoComplete="tel"
                        enterKeyHint="next"
                        value={fields.phone}
                        onChange={setField("phone")}
                        placeholder={c.phonePlaceholder}
                        className={INPUT}
                      />
                    </Field>
                    <Field id="email" label={c.emailAddress} required>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        inputMode="email"
                        required
                        autoComplete="email"
                        autoCapitalize="off"
                        spellCheck={false}
                        enterKeyHint="next"
                        value={fields.email}
                        onChange={setField("email")}
                        placeholder={c.emailPlaceholder2}
                        className={INPUT}
                      />
                    </Field>
                    <Field id="company" label={c.company} optional={c.optional}>
                      <input
                        id="company"
                        name="company"
                        autoComplete="organization"
                        enterKeyHint="next"
                        value={fields.company}
                        onChange={setField("company")}
                        placeholder={c.companyPlaceholder}
                        className={INPUT}
                      />
                    </Field>
                  </div>

                  <ServicePicker
                    label={c.serviceInterest}
                    placeholder={c.selectService}
                    hint={c.servicesHint}
                    doneLabel={c.servicesDone}
                    clearLabel={c.servicesClear}
                    groups={serviceGroups}
                    selected={selectedServices}
                    onToggle={toggleService}
                    onClear={() => setFields((f) => ({ ...f, services: [] }))}
                  />

                  <Field id="message" label={c.message} required>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      value={fields.message}
                      onChange={setField("message")}
                      placeholder={c.messagePlaceholder}
                      className={`${CONTROL} resize-none p-4 leading-relaxed`}
                    />
                  </Field>

                  <FormGuard ref={guard} />

                  {formStatus === "error" && (
                    <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-400/25 bg-red-400/[0.07] p-3 text-sm text-red-300">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <div className="flex flex-col gap-4 border-t border-white/[0.06] pt-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                    <p className="order-2 sm:order-1 max-w-sm text-xs sm:text-[13px] text-white/35 leading-relaxed">
                      {t.formPrivacy.before}
                      <Link
                        href="/privacy"
                        className="text-white/65 underline underline-offset-2 transition-colors duration-150 hover:text-white"
                      >
                        {t.formPrivacy.link}
                      </Link>
                      {t.formPrivacy.after}
                    </p>
                    <button
                      type="submit"
                      disabled={formStatus === "loading"}
                      className="group order-1 sm:order-2 relative inline-flex h-[52px] w-full sm:w-auto shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full px-8 text-[15px] font-semibold text-white shadow-[0_12px_40px_-12px_rgba(224,64,160,0.7)] transition-all duration-200 hover:shadow-[0_16px_48px_-12px_rgba(224,64,160,0.9)] active:scale-[0.98] disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
                      style={{ background: "linear-gradient(135deg, #E040A0 0%, #FF6F91 55%, #FFB76C 100%)" }}
                    >
                      {formStatus === "loading" ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          {c.sending}
                        </>
                      ) : (
                        <>
                          {c.submit}
                          <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                        </>
                      )}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ── Info ── */}
          <motion.aside
            className="order-first lg:order-none lg:col-span-4 lg:sticky lg:top-32 flex flex-col gap-4 sm:gap-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          >
            <div className="rounded-[22px] sm:rounded-[28px] border border-white/[0.08] bg-white/[0.02] p-2 sm:p-3">
              <ContactRow icon={Mail} label={c.emailLabel} value={EMAIL} href={`mailto:${EMAIL}`} />
              <div className="mx-3 h-px bg-white/[0.06]" />
              <ContactRow icon={Phone} label={c.phoneLabel} value={PHONE_DISPLAY} href={PHONE_HREF} />
              <div className="mx-3 h-px bg-white/[0.06]" />
              <ContactRow icon={MapPin} label={c.locationLabel} value={c.location} />
            </div>
            <div
              className="relative overflow-hidden rounded-[22px] sm:rounded-[28px] border border-[#E040A0]/25 p-5 sm:p-7"
              style={{ background: "linear-gradient(140deg, rgba(224,64,160,0.12), rgba(155,89,245,0.08))" }}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(ellipse 90% 70% at 50% 120%, rgba(224,64,160,0.18), transparent 70%)" }}
              />
              <div className="relative">
                <Image
                  src="/aviva-digital-white-logo.webp"
                  alt="Aviva Digital"
                  width={140}
                  height={56}
                  className="mb-4 h-10 sm:h-12 w-auto"
                />
                <p className="text-[15px] text-white/65 leading-relaxed">{c.infoTagline}</p>
                <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-[13px] text-emerald-200">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  <Clock size={13} />
                  {c.responseNote}
                </span>
              </div>
            </div>

          </motion.aside>
        </div>
      </div>
    </section>
  );
}
